import { useGenerateWithAIForm } from "@/content/ai/GenerateWithAIForm"
import GenerateWithAIModal from "@/content/ai/GenerateWithAIModal"
import { EXAMPLE_CHAT_PLACEHOLDER } from "@/content/ai/utils/AIGenerationUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoIcon, DiscoIconButton, DiscoSpinner, DiscoTooltip } from "@disco-ui"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import { useTheme } from "@material-ui/core"
import { useState } from "react"
import { CreateMessageWithAIQueryParams } from "../CreateMessageWithAIButton"

interface Props {
  chatChannelId: string
  isGeneratingAiMessage?: boolean
}

function GenerateChatMessageWithAIButton({
  chatChannelId,
  isGeneratingAiMessage,
}: Props) {
  const classes = useStyles()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const theme = useTheme()
  const iconColor =
    theme.palette.type === "dark"
      ? theme.palette.groovy.grey[100]
      : theme.palette.groovy.grey[500]

  const { form } = useGenerateWithAIForm()

  const [_, setParams] = useQueryParamState<CreateMessageWithAIQueryParams>()

  if (isGeneratingAiMessage) {
    return (
      <div className={classes.aiButton}>
        <DiscoSpinner size={"sm"} />
      </div>
    )
  }

  return (
    <>
      <DiscoTooltip content={"Write with AI"}>
        <DiscoIconButton
          onClick={handleOpenModal}
          className={classes.aiButton}
          width={"20px"}
          height={"20px"}
          color={iconColor}
        >
          <DiscoIcon icon={"iconsax.custom-stars"} />
        </DiscoIconButton>
      </DiscoTooltip>

      <GenerateWithAIModal
        testid={"GenerateChatMessageWithAI"}
        modalContentLabel={"Generate AI chat message"}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={"Describe your message"}
        form={form}
        onSubmit={onSubmitAiForm}
        placeholder={EXAMPLE_CHAT_PLACEHOLDER}
      />
    </>
  )

  function handleOpenModal() {
    setIsModalOpen(true)
  }

  function handleCloseModal() {
    form.reset()
    setIsModalOpen(false)
  }

  function onSubmitAiForm() {
    const {
      prompt,
      referenceUrls,
      searchWeb,
      assetEmbeddingSourceIds,
      contentEmbeddingSourceIds,
      trainingDataEmbeddingSourceIds,
      communityContext,
    } = form.state

    if (!chatChannelId || !prompt) {
      console.error("Prompt is empty or channelId is missing!")
      return
    }

    const allReferenceEmbeddingSourceIds = [
      ...(assetEmbeddingSourceIds || []),
      ...(contentEmbeddingSourceIds || []),
      ...(trainingDataEmbeddingSourceIds || []),
    ]

    setParams({
      aiGid: chatChannelId,
      aiPrompt: prompt,
      aiReferenceUrls:
        referenceUrls && referenceUrls.length > 0
          ? JSON.stringify(referenceUrls)
          : undefined,
      aiSearchWeb: searchWeb ? "1" : "0",
      aiCommunityContext: communityContext ? "1" : "0",
      aiReferenceEmbeddingSourceIds:
        allReferenceEmbeddingSourceIds.length > 0
          ? JSON.stringify(allReferenceEmbeddingSourceIds)
          : undefined,
      aiTemplate: undefined,
    })

    handleCloseModal()
  }
}

const useStyles = makeUseStyles({
  aiButton: {
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
})

export default GenerateChatMessageWithAIButton
